import React, {ChangeEvent, CSSProperties, memo, useState} from 'react';
import {ReactComponent as OnlineClassIcon} from '../../assets/login/online-class.svg';
import {useInputWithFocus} from "common/hook/useInputWithFocus";
import {Link} from "react-router-dom";
import {
    DATA_POLICY,
    LOGIN_BUTTON_TEXT,
    REGISTER_BUTTON_TEXT,
    REGISTER_TEXT,
    TERMS_AND_CONDITIONS,
} from "entities/constants/constants";
import {useAppDispatch, useAppSelector} from "common/hook";
import {
    ExclamationCircleOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,

} from '@ant-design/icons';
import {getRegisterState} from "features/model/selector/get-register-state/getRegisterState";
import {registerByUser} from "features/model/service/register-by-user/registerByUser";
import {registerActions} from "features/model/slice/registerSlice";
import {useFormik} from "formik";

import {Button, Tooltip} from "antd";


export const RegisterForm = memo(() => {

    const dispatch = useAppDispatch();
    const {
        firstName,
        lastName,
        email,
        phone,
        password,
        retypePassword,
        status,
        privacyAccept,
        promoCode,
        errors,
    } = useAppSelector(getRegisterState);

    const [passwordVisible, setPasswordVisible] = useState(false);
    const [retypePasswordVisible, setRetypePasswordVisible] = useState(false);

    const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
    const toggleRetypePasswordVisibility = () => setRetypePasswordVisible(!retypePasswordVisible);


    const {isFilled, handleFocus, handleBlur} = useInputWithFocus([
        false,
        false,
        false,
        false,
        false,
        false,
        false,
    ]);

    const handleMobilePhoneInput = (event: ChangeEvent<HTMLInputElement>) => {
        const {value} = event.target;
        event.target.value = value.replace(/[^0-9+()-]/g, "");
    };


    const formik = useFormik({
        initialValues: {
            firstName,
            lastName,
            email,
            phone,
            password,
            retypePassword,
            privacyAccept,
            promoCode,
            website: '',
        },
        onSubmit: async (values) => {
            if (values.website.trim()) {
                return;
            }

            dispatch(registerActions.setFirstName(values.firstName));
            dispatch(registerActions.setLastName(values.lastName));
            dispatch(registerActions.setEmail(values.email));
            dispatch(registerActions.setPhone(values.phone));
            dispatch(registerActions.setPassword(values.password));
            dispatch(registerActions.setRetypePassword(values.retypePassword));
            dispatch(registerActions.setPrivacyAccept(values.privacyAccept));
            dispatch(registerActions.setPromoCode(values.promoCode));
            await dispatch(registerByUser(values));
        },
    });

    const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;


        if (errors[name]) {
            dispatch(registerActions.clearFieldError(name));
        }

        formik.handleChange(e);
    };

    const isButtonDisabled = !formik.values.privacyAccept;

    const overlayInnerStyle: CSSProperties = {
        borderRadius: '20px',
        border: '1px solid #E6EAEB',
        backgroundColor: '#fafafc',
        color: '#002E33',
        padding: '10px 15px',
        textAlign: 'center'
    }

    return (
        <div className="app service-page">
            <div className="bg-box"><OnlineClassIcon/></div>
            <div className="form-box">
                <div className="sub-box">
                    <p className="h3 popup-title">{REGISTER_TEXT}</p>
                    <form
                        className="form"
                        onSubmit={formik.handleSubmit}
                    >
                        <div
                            className={`field required ${isFilled[0] ? 'filled' : ''} ${errors.firstName ? 'error-field' : ''}`}>
                            <label className={`field-name ${formik.values.firstName ? 'active-label' : ''}`}>Имя</label>
                            <div className="inp-box">
                                <input
                                    type="text"
                                    name='firstName'
                                    className="form-control"
                                    value={formik.values.firstName}
                                    onFocus={() => handleFocus(0)}
                                    onChange={handleFieldChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            {errors.firstName && <p className="field-error">{errors.firstName}</p>}
                        </div>
                        <div
                            className={`field required ${isFilled[1] ? 'filled' : ''} ${errors.lastName ? 'error-field' : ''}`}>
                            <label
                                className={`field-name ${formik.values.lastName ? 'active-label' : ''}`}>Фамилия</label>
                            <div className="inp-box">
                                <input
                                    type="text"
                                    name='lastName'
                                    value={formik.values.lastName}
                                    className="form-control"
                                    onFocus={() => handleFocus(1)}
                                    onChange={handleFieldChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            {errors.lastName && <p className="field-error">{errors.lastName}</p>}
                        </div>

                        <div
                            className={`field required ${isFilled[2] ? 'filled' : ''} ${errors.email ? 'error-field' : ''}`}>
                            <label
                                className={`field-name ${formik.values.email ? 'active-label' : ''}`}>Email</label>
                            <div className="inp-box">
                                <input
                                    type="text"
                                    name='email'
                                    value={formik.values.email}
                                    className="form-control"
                                    onFocus={() => handleFocus(2)}
                                    onChange={handleFieldChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            {errors.email && <p className="field-error">{errors.email}</p>}
                        </div>

                        <div
                            className={`field required ${isFilled[3] ? 'filled' : ''} ${errors.phone ? 'error-field' : ''}`}>
                            <label className={`field-name active-label`}>Контактный телефон</label>
                            <div className="inp-box">
                                <input
                                    type="text"
                                    name='phone'
                                    value={formik.values.phone}
                                    className="form-control"
                                    onChange={handleFieldChange}
                                    onFocus={() => handleFocus(3)}
                                    pattern="\+?[0-9\s\-\(\)]+"
                                    onInput={handleMobilePhoneInput}
                                    placeholder="+375 (XX) XXX - XX - XX"
                                />

                                <div className="tooltip Tooltip">
                                    <Tooltip placement="top"
                                             className='tooltip-block'
                                             overlayInnerStyle={overlayInnerStyle}
                                             color='#fafafc'
                                             title={'Введите номер телефона в формате +375(XX)XXX-XX-XX'}
                                    >
                                        <Button className={'tooltip-btn'}><ExclamationCircleOutlined/></Button>
                                    </Tooltip>
                                </div>
                            </div>
                            {errors.phone && <p className="field-error">{errors.phone}</p>}
                        </div>

                        <div
                            className={`field required ${isFilled[4] ? 'filled' : ''} ${errors.password ? 'error-field' : ''}`}>
                            <label
                                className={`field-name ${formik.values.password ? 'active-label' : ''}`}>Пароль</label>
                            <div className="inp-box-password password-box">
                                <input
                                    type={passwordVisible ? 'text' : 'password'}
                                    name="password"
                                    value={formik.values.password}
                                    onChange={handleFieldChange}
                                    className="form-control"
                                    onFocus={() => handleFocus(4)}
                                    onBlur={handleBlur}
                                />
                                {/*<div className="tooltip Tooltip">*/}
                                {/*    <Tooltip placement="topRight" className='tooltip-block'*/}
                                {/*             title={'Минимальные требования к сложности пароля: 6 знаков, минимум 1 буква, 1 цифра'}*/}
                                {/*    >*/}
                                {/*        <Button className={'tooltip-btn'}><ExclamationCircleOutlined/></Button>*/}
                                {/*    </Tooltip>*/}
                                {/*</div>*/}
                                <div className="tooltip Tooltip">
                                    <span
                                        className="toggle-password-visibility visibility_toggle-password"
                                        onClick={togglePasswordVisibility}
                                    >
                                    {passwordVisible ? <EyeInvisibleOutlined/> : <EyeOutlined/>}
                                 </span>
                                    <Tooltip placement="top"
                                             overlayInnerStyle={overlayInnerStyle}
                                             color='#fafafc'
                                             title={'Минимальные требования к сложности пароля: 6 знаков, минимум 1 буква, 1 цифра'}
                                    >
                                        <Button className={'tooltip-btn'}><ExclamationCircleOutlined/></Button>
                                    </Tooltip>
                                    {/*<div className="new-tooltip">*/}
                                    {/*     <span className="icon pointer-elem">*/}
                                    {/*            <NoticeIcon/>*/}
                                    {/*     </span>*/}
                                    {/*    <div className="tooltip-text">*/}
                                    {/*        Минимальные требования к сложности пароля: <br/> 6 знаков, минимум 1 буква,*/}
                                    {/*        1 цифра*/}
                                    {/*    </div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>
                            {errors.password && <p className="field-error">{errors.password}</p>}
                        </div>

                        <div
                            className={`field required ${isFilled[5] ? 'filled' : ''} ${errors.retypePassword ? 'error-field' : ''}`}>
                            <label className={`field-name ${formik.values.retypePassword ? 'active-label' : ''}`}>Повторите
                                пароль</label>
                            <div className="inp-box-password password-box">
                                <input
                                    type={retypePasswordVisible ? 'text' : 'password'}
                                    name="retypePassword"
                                    value={formik.values.retypePassword}
                                    className="form-control"
                                    onFocus={() => handleFocus(5)}
                                    onChange={handleFieldChange}
                                    onBlur={handleBlur}
                                />
                                <div className="tooltip Tooltip">
                                    <span
                                        className="toggle-password-visibility visibility_toggle-password"
                                        onClick={toggleRetypePasswordVisibility}
                                    >
                                    {retypePasswordVisible ? <EyeInvisibleOutlined/> : <EyeOutlined/>}
                                 </span>
                                    <Tooltip placement="top"
                                             overlayInnerStyle={overlayInnerStyle}
                                             color='#fafafc'
                                             title={'Повторите пароль'}
                                    >
                                        <Button className={'tooltip-btn'}><ExclamationCircleOutlined/></Button>
                                    </Tooltip>

                                </div>
                            </div>
                            {errors.retypePassword && <p className="field-error">{errors.retypePassword}</p>}
                        </div>

                        <div className={`field ${isFilled[6] ? 'filled' : ''}`}>
                            <label
                                className={`field-name ${formik.values.promoCode ? 'active-label' : ''}`}>Промокод</label>
                            <div className="inp-box">
                                <input
                                    type="text"
                                    name='promoCode'
                                    value={formik.values.promoCode}
                                    className="form-control"
                                    onFocus={() => handleFocus(6)}
                                    onChange={formik.handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>
                        <div
                            aria-hidden="true"
                            style={{position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden'}}
                        >
                            <label htmlFor="website">Website</label>
                            <input
                                id="website"
                                type="text"
                                name="website"
                                value={formik.values.website}
                                onChange={formik.handleChange}
                                autoComplete="off"
                                tabIndex={-1}
                            />
                        </div>
                        <div className="checkbox-item no-left-indent">
                            <input
                                id="agreement-inp"
                                type="checkbox"
                                name='privacyAccept'
                                checked={formik.values.privacyAccept}
                                onChange={formik.handleChange}
                            />


                            <label htmlFor="agreement-inp">
                                <span className="checkbox-left-indent">
                                    Я согласен(a) с <a href={TERMS_AND_CONDITIONS} target="_blank">договором оферты</a> и
                                    обработкой{' '}
                                    <a href={DATA_POLICY} target="_blank">персональных данных</a>
                                </span>

                            </label>


                        </div>
                        <button type="submit" className="btn full-width" disabled={isButtonDisabled}>
                            {
                                status === 'pending'
                                    ? <span className="loader"></span>
                                    : <span>{REGISTER_BUTTON_TEXT}</span>
                            }
                        </button>
                        <div className="form-prompt">
                            <p>Уже есть аккаунт? <Link to="/">{LOGIN_BUTTON_TEXT}</Link></p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
});

